import React, { useEffect, useRef, useState } from 'react';

import Icon from '~go/web/src/js/components/common/icon/icon';
import { COLOR_NAMES } from '~go/web/src/js/constants/colors';
import { clsx } from '../../../utils/clsx/clsx';

const sizeMap = {
  small: 'btn-sm',
  default: 'btn-default',
  large: 'btn-lg',
} as const;

const classNameMap = {
  solid: {
    primary: 'btn btn-primary',
    basic: 'btn btn-basic',
    destructive: 'btn btn-danger',
    opportunity: 'btn btn-opportunity',
    none: '',
  },
  outline: {
    primary: 'btn btn-outline-primary',
    basic: 'btn btn-outline-basic',
    destructive: 'btn btn-outline-danger',
    opportunity: 'btn btn-outline-opportunity',
    none: '',
  },
  outlineFill: {
    primary: 'btn btn-outline-fill-primary',
    basic: 'btn btn-outline-fill-basic',
    destructive: 'btn btn-outline-fill-danger',
    opportunity: 'btn btn-outline-fill-opportunity',
    none: '',
  },
  link: {
    primary: 'btn btn-link btn-link-primary',
    basic: 'btn btn-link btn-link-basic',
    destructive: 'btn btn-link btn-link-danger',
    opportunity: 'btn btn-link btn-link-opportunity',
    none: '',
  },
} as const;

export type Purpose = 'primary' | 'basic' | 'destructive' | 'opportunity' | 'none';
export type DesignType = 'solid' | 'outline' | 'outlineFill' | 'link';
type Size = 'small' | 'default' | 'large';
type ColorName = typeof COLOR_NAMES[number];

// Casting any here to make life easier but we will not have type safety for this component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface ButtonProps extends React.ButtonHTMLAttributes<any> {
  purpose?: Purpose;
  designType?: DesignType;
  size?: Size;
  disabled?: boolean;
  loading?: boolean;
  loadingSpinnerColor?: ColorName;
  component?: React.ElementType;
  style?: React.CSSProperties;
  className?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Button = React.forwardRef<any, ButtonProps>(
  (
    {
      purpose = 'primary',
      designType = 'solid',
      size = 'default',
      disabled = false,
      loading = false,
      loadingSpinnerColor = 'white',
      component = 'button',
      children,
      style = {},
      className = '',
      type = 'button',
      ...props
    },
    ref
  ) => {
    const [width, setWidth] = useState<number>(16);
    const elementRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (elementRef.current) setWidth(elementRef.current.offsetWidth);
    }, []);

    const buttonContent = () => {
      if (designType === 'link') {
        return children;
      }
      return loading ? (
        <>
          <Icon
            icon="circle-notch"
            color={loadingSpinnerColor}
            className="fa-spin"
            style={{ fontSize: '1rem', minWidth: `${width}px` }}
          />
          <div className="d-none">{children}</div>
        </>
      ) : (
        <div ref={elementRef}>{children}</div>
      );
    };

    const Component = component || 'button'; // allow to pass in 'a' for anchor tag type button

    return (
      <Component
        disabled={disabled || loading}
        tabIndex={disabled ? -1 : 0}
        type={type}
        style={style}
        className={clsx(
          classNameMap[designType][purpose],
          loading && 'loading',
          disabled && 'disabled',
          sizeMap[size],
          className
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        ref={ref}
      >
        {buttonContent()}
      </Component>
    );
  }
);
Button.displayName = 'Button';
